import { useCallback, useEffect, useRef, useState } from "react";
import { DisconnectReason } from "livekit-client";
import { rejoinDelayMs } from "../lib/rejoinBackoff";
import { emitCallActivity } from "../lib/callActivity";

/** Call-recorder report of a rejoin step (#2223 J1); no effect on the rejoin. */
const recordRejoin = (n: number, result: "started" | "ok" | "failed") =>
  emitCallActivity("recovery", { trigger: "livekit", step: `rejoin_${n}`, result });

/**
 * Auto-rejoin for the guest-link call (#2223 AC13 for guests).
 *
 * A guest used to land on a manual "Rejoin" screen after any lost
 * connection; now they are brought back like a signed-in user, on the same
 * schedule (lib/rejoinBackoff), with the network coming back or the page
 * becoming visible skipping the wait.
 *
 * A guest has no account to mint a new token with, and asking the guest
 * endpoint again would send them back through the waiting room. So a rejoin
 * reuses the token they were admitted with, by remounting <LiveKitRoom>
 * (`connectKey`). The server sets no TTL, so the token stays valid for
 * hours; once it has expired the guest gets the Rejoin screen (`onExpired`),
 * which starts a real fresh join.
 *
 * Every LiveKitRoom mount reports with its own key: tearing down an old
 * mount can still emit a disconnect, and that must not fail the new attempt.
 */

const WATCHDOG_MS = 15000;

const TERMINAL_REASONS = new Set<DisconnectReason>([
  DisconnectReason.PARTICIPANT_REMOVED,
  DisconnectReason.ROOM_DELETED,
  DisconnectReason.DUPLICATE_IDENTITY,
  DisconnectReason.SERVER_SHUTDOWN,
]);

/** Seconds-since-epoch `exp` of a LiveKit JWT, or null if unreadable. */
export function tokenExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

function isExpired(token: string): boolean {
  const exp = tokenExpiry(token);
  return exp !== null && exp * 1000 <= Date.now();
}

export interface GuestReconnect {
  /** Key for <LiveKitRoom>; a change opens a new connection. */
  connectKey: number;
  reconnecting: boolean;
  handleDisconnected: (key: number, reason?: DisconnectReason) => void;
  /** Connection error on mount (LiveKitRoom onError). */
  handleFailed: (key: number) => void;
  handleConnected: (key: number) => void;
  /** Stop trying (the guest left). */
  cancel: () => void;
}

export function useGuestReconnect({
  token,
  onEnded,
  onExpired,
}: {
  token: string | undefined;
  onEnded: () => void;
  onExpired: () => void;
}): GuestReconnect {
  const [connectKey, setConnectKey] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const keyRef = useRef(0);
  const attemptRef = useRef(0);
  const backoffTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdog = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ token, onEnded, onExpired });
  latest.current = { token, onEnded, onExpired };

  const clearTimers = useCallback(() => {
    if (backoffTimer.current) clearTimeout(backoffTimer.current);
    if (watchdog.current) clearTimeout(watchdog.current);
    backoffTimer.current = null;
    watchdog.current = null;
  }, []);

  const stop = useCallback(() => {
    attemptRef.current = 0;
    clearTimers();
    setReconnecting(false);
  }, [clearTimers]);

  const runRef = useRef<(n: number) => void>(() => {});

  const fail = useCallback(
    (n: number) => {
      if (attemptRef.current !== n) return;
      clearTimers();
      recordRejoin(n, "failed");
      const next = n + 1;
      attemptRef.current = next;
      backoffTimer.current = setTimeout(() => {
        backoffTimer.current = null;
        runRef.current(next);
      }, rejoinDelayMs(next));
    },
    [clearTimers],
  );

  const run = useCallback(
    (n: number) => {
      if (attemptRef.current !== n) return;
      const current = latest.current.token;
      if (!current || isExpired(current)) {
        stop();
        latest.current.onExpired();
        return;
      }
      recordRejoin(n, "started");
      keyRef.current += 1;
      setConnectKey(keyRef.current);
      watchdog.current = setTimeout(() => fail(n), WATCHDOG_MS);
    },
    [fail, stop],
  );
  runRef.current = run;

  const handleDisconnected = useCallback(
    (key: number, reason?: DisconnectReason) => {
      if (key !== keyRef.current) return; // an old mount tearing down
      if (reason !== undefined && TERMINAL_REASONS.has(reason)) {
        stop();
        latest.current.onEnded();
        return;
      }
      if (attemptRef.current === 0) {
        attemptRef.current = 1;
        setReconnecting(true);
        run(1);
      } else {
        fail(attemptRef.current);
      }
    },
    [fail, run, stop],
  );

  const handleFailed = useCallback(
    (key: number) => {
      if (key !== keyRef.current || attemptRef.current === 0) return;
      fail(attemptRef.current);
    },
    [fail],
  );

  const handleConnected = useCallback(
    (key: number) => {
      if (key === keyRef.current && attemptRef.current > 0) {
        recordRejoin(attemptRef.current, "ok");
        stop();
      }
    },
    [stop],
  );

  useEffect(() => {
    const tryNow = () => {
      if (document.visibilityState !== "visible") return;
      if (attemptRef.current === 0 || !backoffTimer.current) return;
      clearTimeout(backoffTimer.current);
      backoffTimer.current = null;
      runRef.current(attemptRef.current);
    };
    window.addEventListener("online", tryNow);
    document.addEventListener("visibilitychange", tryNow);
    return () => {
      window.removeEventListener("online", tryNow);
      document.removeEventListener("visibilitychange", tryNow);
    };
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  return { connectKey, reconnecting, handleDisconnected, handleFailed, handleConnected, cancel: stop };
}
